import { animateBlogArticles } from './blog-articles-animation.js';
import { Component } from './component.js';
import { sectionRenderer } from './section-renderer.js';

class BlogArticlesSortingComponent extends Component {
  /** @type {Array<{element: HTMLElement|null, date: string, title: string, html: string}>} */
  allArticles = [];
  itemsPerPage = 2;
  currentPage = 1;
  totalPages = 1;
  currentSortBy = 'date-desc';
  isLoading = false;
  isInitialized = false;

  connectedCallback() {
    super.connectedCallback();
    if (!this.isInitialized) {
      this.initialize();
    }
  }

  async initialize() {
    if (this.isInitialized) return;
    
    this.isInitialized = true;
    const paginationInfo = this.querySelector('[data-pagination-info]');
    if (paginationInfo instanceof HTMLElement) {
      this.totalPages = parseInt(paginationInfo.dataset.totalPages || '1', 10);
      this.currentPage = parseInt(paginationInfo.dataset.currentPage || '1', 10);
      this.itemsPerPage = parseInt(this.dataset.itemsPerPage || '2', 10);
    }

    const url = new URL(window.location.href);
    const urlPage = url.searchParams.get('page');
    if (urlPage) {
      this.currentPage = parseInt(urlPage, 10);
    }
    
    const urlSort = url.searchParams.get('sort_by');
    if (urlSort) {
      this.currentSortBy = urlSort;
    }

    await this.collectAllArticles();
    
    if (urlSort) {
      this.applySortingAndPagination(this.currentSortBy, this.currentPage);
    }
    
    this.setupSorting();
    this.setupPagination();
  }

  async collectAllArticles() {
    if (this.isLoading) return;
    this.isLoading = true;

    try {
      const resourceList = this.querySelector('[data-testid="featured-blog-posts"]');
      if (resourceList) {
        const currentItems = Array.from(resourceList.querySelectorAll('.resource-list__item'));
        this.allArticles = currentItems.map((item) => ({
          element: item instanceof HTMLElement ? item : null,
          date: item.getAttribute('data-article-date') || '',
          title: item.getAttribute('data-article-title') || '',
          html: item instanceof HTMLElement ? item.outerHTML : ''
        }));
      }

      if (this.totalPages > 1) {
        const sectionId = this.dataset.sectionId;
        if (!sectionId) {
          this.isLoading = false;
          return;
        }

        for (let page = 1; page <= this.totalPages; page++) {
          if (page === this.currentPage) continue;

          try {
            const url = new URL(window.location.href);
            url.searchParams.set('page', page.toString());
            url.searchParams.set('section_id', sectionId);

            const response = await fetch(url.toString());
            if (!response.ok) continue;

            const html = await response.text();
            const parser = new DOMParser();
            const doc = parser.parseFromString(html, 'text/html');
            
            const sectionElement = doc.querySelector(`[data-section-id="${sectionId}"]`);
            if (sectionElement) {
              const pageResourceList = sectionElement.querySelector('[data-testid="featured-blog-posts"]');
              if (pageResourceList) {
                const pageItems = Array.from(pageResourceList.querySelectorAll('.resource-list__item'));
                pageItems.forEach(item => {
                  this.allArticles.push({
                    element: null,
                    date: item.getAttribute('data-article-date') || '',
                    title: item.getAttribute('data-article-title') || '',
                    html: item instanceof HTMLElement ? item.outerHTML : ''
                  });
                });
              }
            }
          } catch (error) {
            // Silently continue if page fails to load
          }
        }
      }
    } finally {
      this.isLoading = false;
    }
  }

  setupSorting() {
    const sortingComponent = this.querySelector('sorting-filter-component');
    if (!sortingComponent) return;

    sortingComponent.addEventListener('change', (event) => {
      if (event.target instanceof HTMLInputElement && event.target.name === 'sort_by') {
        this.sortAndPaginate(event.target.value);
      }
    });
  }

  /** @param {string} sortBy */
  sortAndPaginate(sortBy) {
    this.currentSortBy = sortBy;
    const url = new URL(window.location.href);
    url.searchParams.set('sort_by', sortBy);
    url.searchParams.delete('page');
    window.history.replaceState({}, '', url.toString());
    this.loadPage(1, url); 
  }

  /** @param {string} sortBy @param {number} page */
  applySortingAndPagination(sortBy, page) {
    this.currentSortBy = sortBy;
    this.currentPage = page;

    const sortedArticles = [...this.allArticles].sort((a, b) => {
      switch (sortBy) {
        case 'date-desc':
          return this.compareArticleDates(b, a);
        case 'date-asc':
          return this.compareArticleDates(a, b);
        case 'title-asc':
          return this.compareArticleTitles(a, b);
        case 'title-desc':
          return this.compareArticleTitles(b, a);
        default:
          return 0;
      }
    });

    this.allArticles = sortedArticles;

    this.totalPages = Math.ceil(sortedArticles.length / this.itemsPerPage);
    const startIndex = (this.currentPage - 1) * this.itemsPerPage;
    const endIndex = startIndex + this.itemsPerPage;
    const articlesToShow = sortedArticles.slice(startIndex, endIndex);

    this.updateDOM(articlesToShow);
    this.updateSortingUI(sortBy);
  }

  /** @param {string} sortBy */
  updateSortingUI(sortBy) {
    const sortingComponent = this.querySelector('sorting-filter-component');
    if (!sortingComponent) return;

    const allInputs = sortingComponent.querySelectorAll('input[name="sort_by"]');
    const allOptions = sortingComponent.querySelectorAll('.sorting-filter__option');
    const statusElement = sortingComponent.querySelector('.facets__status');
    let selectedOptionName = '';

    allInputs.forEach((input) => {
      if (input instanceof HTMLInputElement) {
        if (input.value === sortBy) {
          input.checked = true;
          selectedOptionName = input.dataset.optionName || '';
        } else {
          input.checked = false;
        }
      }
    });

    allOptions.forEach((option) => {
      if (!(option instanceof HTMLElement)) return;
      
      const input = option.querySelector('input[name="sort_by"]');
      if (input instanceof HTMLInputElement && input.value === sortBy) {
        option.setAttribute('aria-selected', 'true');
        option.setAttribute('tabindex', '0');
        option.classList.add('active');
      } else {
        option.setAttribute('aria-selected', 'false');
        option.setAttribute('tabindex', '-1');
        option.classList.remove('active');
      }
    });

    if (statusElement && selectedOptionName) {
      statusElement.textContent = selectedOptionName;
    }

    const details = sortingComponent.querySelector('details');
    if (details instanceof HTMLDetailsElement) {
      details.open = false;
    }
  }

  /** @param {Array<{element: HTMLElement|null, date: string, title: string, html: string}>} articlesToShow */
  updateDOM(articlesToShow) {
    const resourceList = this.querySelector('[data-testid="featured-blog-posts"]');
    if (!resourceList) return;

    const initialPositions = new Map();
    const currentItems = Array.from(resourceList.querySelectorAll('.resource-list__item'));
    
    const existingElementsMap = new Map();
    currentItems.forEach((item) => {
      if (item instanceof HTMLElement) {
        const rect = item.getBoundingClientRect();
        const date = item.getAttribute('data-article-date');
        const title = item.getAttribute('data-article-title');
        const key = `${date || ''}_${title || ''}`;
        
        initialPositions.set(item, {
          x: rect.left,
          y: rect.top,
          width: rect.width,
          height: rect.height
        });
        
        existingElementsMap.set(key, item);
      }
    });

    currentItems.forEach(item => item.remove());

    /** @type {Array<{element: HTMLElement, initialPos: {x: number, y: number, width: number, height: number}|null}>} */
    const newElements = [];
    articlesToShow.forEach(/** @param {{element: HTMLElement|null, date: string, title: string, html: string}} articleData */ (articleData) => {
      let element;
      const key = `${articleData.date || ''}_${articleData.title || ''}`;
      
      const existingElement = existingElementsMap.get(key);
      if (existingElement && document.contains(existingElement) && existingElement instanceof HTMLElement) {
        element = existingElement;
      } else {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = articleData.html;
        element = tempDiv.firstElementChild;
      }
      
      if (element instanceof HTMLElement) {
        resourceList.appendChild(element);
        newElements.push({
          element,
          initialPos: existingElement && existingElement instanceof HTMLElement ? initialPositions.get(existingElement) || null : null
        });
      }
    });

    animateBlogArticles(newElements, resourceList);
  }

  setupPagination() {
    const paginationWrapper = this.querySelector('.pagination-wrapper');
    if (!paginationWrapper) return;

    paginationWrapper.addEventListener('click', (event) => {
      if (!(event.target instanceof HTMLElement)) return;
      const link = event.target.closest('a.pagination__item');
      if (!(link instanceof HTMLAnchorElement)) return;

      if (link.hasAttribute('aria-disabled') || link.classList.contains('pagination__item--current')) {
        return;
      }

      const ariaLabel = link.getAttribute('aria-label');
      if (ariaLabel === 'Previous' && link.classList.contains('active')) {
        event.preventDefault();
        event.stopPropagation();
        const prevPage = this.currentPage - 1;
        if (prevPage >= 1) {
          const url = new URL(window.location.href);
          url.searchParams.set('page', prevPage.toString());
          this.loadPage(prevPage, url);
        }
        return;
      }

      if (ariaLabel === 'Next' && link.classList.contains('active')) {
        event.preventDefault();
        event.stopPropagation();
        const nextPage = this.currentPage + 1;
        if (nextPage <= this.totalPages) {
          const url = new URL(window.location.href);
          url.searchParams.set('page', nextPage.toString());
          this.loadPage(nextPage, url);
        }
        return;
      }

      if (link.classList.contains('pagination__item-arrow') && !link.classList.contains('active')) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const href = link.getAttribute('href');
      if (!href) return;

      const url = new URL(href, window.location.origin);
      const pageParam = url.searchParams.get('page');
      if (!pageParam) return;

      const pageNumber = parseInt(pageParam, 10);
      if (isNaN(pageNumber)) return;

      this.loadPage(pageNumber, url); 
    });
  }

  /** @param {number} pageNumber @param {URL} url */
  async loadPage(pageNumber, url) {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) return;

    const savedSortBy = this.currentSortBy;

    if (this.allArticles.length > 0) {
      if (savedSortBy) {
        url.searchParams.set('sort_by', savedSortBy);
      }

      window.history.pushState({}, '', url.toString());
    }

    try {
      await sectionRenderer.renderSection(sectionId, { url });

      this.isInitialized = false;
      this.applySortingAndPagination(savedSortBy, pageNumber);
    } catch (error) {
      window.location.href = url.toString();
    }
  }

  /** @param {{date: string}} a @param {{date: string}} b */
  compareArticleDates(a, b) {
    const dateA = a.date ? new Date(a.date) : null;
    const dateB = b.date ? new Date(b.date) : null;
    if (!dateA || !dateB) return 0;
    return dateA.getTime() - dateB.getTime();
  }

  /** @param {{title: string}} a @param {{title: string}} b */
  compareArticleTitles(a, b) {
    const titleA = a.title || '';
    const titleB = b.title || '';
    if (!titleA || !titleB) return 0;
    return titleA.localeCompare(titleB, undefined, { sensitivity: 'base' });
  }
}

if (!customElements.get('blog-articles-sorting-component')) {
  customElements.define('blog-articles-sorting-component', BlogArticlesSortingComponent);
}
